/**
 * Admin auth — session probe via api-worker /api/auth/session.
 *
 * The session cookie is httpOnly + Secure + SameSite=Lax, so the SPA can't
 * read it directly. Instead, every page mount probes /api/auth/session
 * and the result populates a TanStack Query cache. Mutations
 * (login/logout/operator-login) invalidate the same key.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type Role = 'owner' | 'manager' | 'receptionist' | 'read_only' | 'operator';

export interface SessionInfo {
  user: { id: string; email: string; full_name: string | null };
  tenant: { id: string; slug: string; legal_name: string } | null;
  role: Role;
  expires_at: number;
}

export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

async function fetchSession(): Promise<SessionInfo | null> {
  const res = await api.api.auth.session.$get();
  if (!res.ok) return null;
  const json = (await res.json()) as { session: SessionInfo | null };
  return json.session;
}

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string; tenant_slug?: string }) => {
      const res = await api.api.auth.login.$post({ json: input });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'login_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

export function useOperatorLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { token: string }) => {
      const res = await api.api.auth['operator-login'].$post({ json: input });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'operator_login_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.api.auth.logout.$post();
    },
    onSuccess: () => {
      qc.setQueryData(SESSION_QUERY_KEY, null);
      qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}
